import { Component, ChangeDetectionStrategy, effect, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import mapboxgl, { Map as MapboxMap, Marker as MapboxMarker } from 'mapbox-gl';
import { Settings } from '@dispo-web/settings/settings';
import { TranslateModule } from '@ngx-translate/core';
import { GeocodingFocusPoint } from '@tourfold/libraries/tf-api-client-public-v2';
import { MapboxMapComponent } from '@tourfold/libraries/shared-ui';

@Component({
  selector: 'tf-settings-geocoding-map',
  imports: [CommonModule, FormsModule, ButtonModule, TranslateModule, MapboxMapComponent],
  templateUrl: './geocoding-map.html',
  styleUrl: './geocoding-map.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeocodingMap {
  readonly mapDefaults = Settings.getSettings().map;

  readonly focusPoint = input<GeocodingFocusPoint | null>(null);
  readonly focusPointChange = output<GeocodingFocusPoint>();

  readonly initialCenter: [number, number] = [
    this.mapDefaults.defaultLocation.lon,
    this.mapDefaults.defaultLocation.lat,
  ];
  readonly defaultZoom = 6;
  // Zoom applied when an external update lands a focus point — close enough to verify
  // the location, wide enough to keep the surrounding area visible. User can zoom further.
  private readonly externalUpdateZoom = 13;

  private map: MapboxMap | null = null;
  private marker: MapboxMarker | null = null;
  // True while the parent's focusPoint update is the round-trip from a user gesture
  // inside the map — used to skip recentering on the round-trip.
  private isEmittingFromMap = false;

  constructor() {
    effect(() => {
      const focusPoint = this.focusPoint();
      if (!focusPoint || !this.map) {
        return;
      }
      this.placeMarker(focusPoint.lat, focusPoint.lon);
      if (!this.isEmittingFromMap) {
        this.map.flyTo({
          center: [focusPoint.lon, focusPoint.lat],
          zoom: this.externalUpdateZoom,
          essential: true,
        });
      }
    });
  }

  onMapLoad(mapInstance: MapboxMap): void {
    this.map = mapInstance;

    requestAnimationFrame(() => mapInstance.resize());

    const focusPoint = this.focusPoint();
    if (focusPoint) {
      mapInstance.setCenter([focusPoint.lon, focusPoint.lat]);
      mapInstance.setZoom(this.externalUpdateZoom);
      this.placeMarker(focusPoint.lat, focusPoint.lon);
    }

    // Click anywhere on the map → move the pin to the clicked point.
    mapInstance.on('click', (event) => {
      const { lat, lng } = event.lngLat;
      this.placeMarker(lat, lng);
      this.emit(lat, lng);
    });
  }

  private placeMarker(lat: number, lon: number): void {
    if (!this.map) {
      return;
    }
    if (!this.marker) {
      this.marker = new mapboxgl.Marker({ draggable: true }).setLngLat([lon, lat]).addTo(this.map);
      this.marker.on('dragend', () => {
        const lngLat = this.marker?.getLngLat();
        if (!lngLat) return;
        this.emit(lngLat.lat, lngLat.lng);
      });
    } else {
      this.marker.setLngLat([lon, lat]);
    }
  }

  private emit(lat: number, lon: number): void {
    this.isEmittingFromMap = true;
    this.focusPointChange.emit({ lat, lon });
    // Reset after a microtask so the round-trip effect can see the flag.
    setTimeout(() => {
      this.isEmittingFromMap = false;
    }, 0);
  }
}
